/**
 * AdminHelp - Contextual "How to use this" help for admin tools
 * 
 * Reusable component that shows a small "?" or "i" icon.
 * Clicking opens a modal with SOP-style guidance.
 * 
 * Usage:
 *   <AdminHelp helpKey="payouts" />
 *   <AdminHelp helpKey="room_topics" size="md" />
 * 
 * Props:
 *   helpKey: Key from ADMIN_HELP content (required)
 *   size: 'xs' | 'sm' | 'md' (default: 'sm')
 *   className: Additional CSS classes
 *   variant: 'icon' | 'button' (default: 'icon')
 */

import React, { useState, useEffect, useCallback } from 'react';
import { HelpCircle, X, AlertTriangle, XCircle, Lightbulb } from 'lucide-react';
import { ADMIN_HELP } from '../../content/adminHelpContent';

function AdminHelpModal({ isOpen, onClose, content }) {
  // Handle ESC key
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen || !content) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="admin-help-modal-backdrop"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      
      {/* Modal */}
      <div
        className="relative w-full max-w-lg bg-slate-900 rounded-2xl shadow-2xl border border-slate-700 overflow-hidden max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-help-title"
        data-testid="admin-help-modal"
      >
        {/* Header */}
        <div className="sticky top-0 flex items-start justify-between p-4 pb-3 border-b border-slate-700 bg-slate-900">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-purple-500/20">
              <HelpCircle className="w-5 h-5 text-purple-400" />
            </div>
            <h2 
              id="admin-help-title" 
              className="text-lg font-semibold text-white"
              data-testid="admin-help-title"
            >
              {content.title}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors"
            aria-label="Close"
            data-testid="admin-help-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* What it does */}
          <div className="space-y-2">
            <h3 className="text-xs font-medium text-slate-400 uppercase tracking-wider">
              What it does
            </h3>
            {content.bullets?.map((line, i) => (
              <p 
                key={i} 
                className="text-sm text-slate-300 leading-relaxed flex items-start gap-2"
                data-testid="admin-help-bullet"
              >
                <span className="text-purple-400 mt-1">•</span>
                <span>{line}</span>
              </p>
            ))}
          </div>

          {/* Warnings */}
          {content.warnings?.length > 0 && (
            <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 space-y-2">
              <div className="flex items-center gap-2 text-amber-400">
                <AlertTriangle className="w-4 h-4" />
                <h3 className="text-xs font-medium uppercase tracking-wider">
                  Watch out for
                </h3>
              </div>
              {content.warnings.map((line, i) => (
                <p 
                  key={i} 
                  className="text-sm text-amber-200/90 leading-relaxed flex items-start gap-2"
                  data-testid="admin-help-warning"
                >
                  <span className="text-amber-400 mt-1">•</span>
                  <span>{line}</span>
                </p>
              ))}
            </div>
          )}

          {/* Common mistakes */}
          {content.mistakes?.length > 0 && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 space-y-2">
              <div className="flex items-center gap-2 text-red-400">
                <XCircle className="w-4 h-4" />
                <h3 className="text-xs font-medium uppercase tracking-wider">
                  Common mistakes
                </h3>
              </div>
              {content.mistakes.map((line, i) => (
                <p 
                  key={i} 
                  className="text-sm text-red-200/90 leading-relaxed flex items-start gap-2"
                  data-testid="admin-help-mistake"
                >
                  <span className="text-red-400 mt-1">✗</span>
                  <span>{line}</span>
                </p>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 px-4 pb-4 pt-2 bg-slate-900">
          <button
            onClick={onClose}
            className="w-full py-2.5 px-4 text-sm font-medium text-white bg-purple-600 hover:bg-purple-500 rounded-xl transition-colors"
            data-testid="admin-help-got-it"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminHelp({ 
  helpKey, 
  className = '',
  size = 'sm',
  variant = 'icon'
}) {
  const [showModal, setShowModal] = useState(false);
  
  const content = ADMIN_HELP[helpKey];
  
  const sizeClasses = {
    xs: 'w-4 h-4 min-w-[16px] min-h-[16px]',
    sm: 'w-5 h-5 min-w-[20px] min-h-[20px]',
    md: 'w-6 h-6 min-w-[24px] min-h-[24px]',
  };
  
  const iconSizes = {
    xs: 'w-2.5 h-2.5',
    sm: 'w-3 h-3',
    md: 'w-3.5 h-3.5',
  };

  const handleClick = (e) => {
    e.stopPropagation();
    setShowModal(true);
  };

  if (!content) {
    console.warn(`[AdminHelp] No content found for key: ${helpKey}`);
    return null;
  }

  if (variant === 'button') {
    return (
      <>
        <button
          type="button"
          onClick={handleClick}
          className={`
            inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs
            bg-slate-800 hover:bg-slate-700 
            text-slate-400 hover:text-slate-200
            border border-slate-700 hover:border-slate-600
            transition-colors duration-150
            ${className}
          `}
          aria-label={`How to use ${content.title}`}
          title="How to use this"
          data-testid={`admin-help-btn-${helpKey}`}
        >
          <HelpCircle className="w-3.5 h-3.5" />
          <span>How to use</span>
        </button>

        <AdminHelpModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          content={content}
        />
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={`
          inline-flex items-center justify-center rounded-full
          bg-slate-700/60 hover:bg-slate-600
          text-slate-400 hover:text-slate-200
          transition-colors duration-150
          focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:ring-offset-1 focus:ring-offset-slate-900
          ${sizeClasses[size]}
          ${className}
        `}
        aria-label={`How to use ${content.title}`}
        title="How to use this"
        data-testid={`admin-help-${helpKey}`}
      >
        <HelpCircle className={iconSizes[size]} />
      </button>

      <AdminHelpModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        content={content}
      />
    </>
  );
}
